import React, { useEffect, useRef, useState } from "react";
import Layout from "../../components/Layout.tsx";
import Title from "../../components/Title.tsx";
import ModalContainer from "../../components/ModalContainer.tsx";
import { notification } from "../../utils/Notifications.tsx";
import { readBranches } from "../../services/branch.service.ts";
import {
  PartnerDiagnosis,
  PartnerSearchResult,
  RecoveryEvaluation,
  RecoveryHistoryPage,
  RecoveryResult,
  evaluatePartnerRecovery,
  getPartnerDiagnosis,
  listIncidentRecoveries,
  recoverPartner,
  searchIncidentPartners,
} from "../../services/incidentRecovery.service.ts";

const operationLabel = "Reactivar colaborador";

function blockersFrom(error: any): string[] {
  const blockers = error?.blockers ?? error?.message;
  if (Array.isArray(blockers)) return blockers.map(String);
  if (typeof blockers === "string") return [blockers];
  return ["No se pudo completar la recuperación"];
}

const IncidentRecoveryPage: React.FC = () => {
  const [query, setQuery] = useState("");
  const [branchId, setBranchId] = useState("");
  const [branches, setBranches] = useState<Array<{ id: number; name: string }>>([]);
  const [results, setResults] = useState<PartnerSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [diagnosis, setDiagnosis] = useState<PartnerDiagnosis | null>(null);
  const [loadingDiagnosis, setLoadingDiagnosis] = useState(false);
  const [evaluation, setEvaluation] = useState<RecoveryEvaluation | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [justification, setJustification] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState<RecoveryResult | null>(null);
  const [history, setHistory] = useState<RecoveryHistoryPage | null>(null);
  const [historyPage, setHistoryPage] = useState(1);
  const submittingRef = useRef(false);

  const loadHistory = async (page = historyPage) => {
    try {
      setHistory(await listIncidentRecoveries(page, 10));
    } catch (error) {
      notification("Error", "No se pudo cargar el historial de recuperaciones", "danger");
    }
  };

  useEffect(() => {
    let active = true;
    readBranches()
      .then((rows) => {
        if (active) setBranches(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (active) setBranches([]);
      });
    listIncidentRecoveries(1, 10)
      .then((page) => {
        if (active) setHistory(page);
      })
      .catch(() => {
        if (active) {
          notification("Error", "No se pudo cargar el historial de recuperaciones", "danger");
        }
      });
    return () => {
      active = false;
    };
  }, []);

  const search = async () => {
    const term = query.trim();
    if (!term) return;
    setSearching(true);
    try {
      const rows = await searchIncidentPartners(
        term,
        branchId ? Number(branchId) : undefined,
      );
      setResults(rows);
    } catch (error) {
      notification("Error", "No se pudo buscar al colaborador", "danger");
    } finally {
      setSearching(false);
    }
  };

  const openDiagnosis = async (partnerId: number) => {
    setLoadingDiagnosis(true);
    setLastResult(null);
    setEvaluation(null);
    try {
      setDiagnosis(await getPartnerDiagnosis(partnerId));
    } catch (error) {
      notification("Error", "No se pudo consultar el diagnóstico", "danger");
    } finally {
      setLoadingDiagnosis(false);
    }
  };

  const openRecovery = async () => {
    if (!diagnosis) return;
    try {
      const fresh = await evaluatePartnerRecovery(diagnosis.partnerId);
      setEvaluation(fresh);
      if (!fresh.eligible) {
        notification(
          "Recuperación bloqueada",
          fresh.blockers[0] ?? "La operación ya no es segura",
          "warning",
        );
        setDiagnosis(await getPartnerDiagnosis(diagnosis.partnerId));
        return;
      }
      setJustification("");
      setConfirmed(false);
      setModalOpen(true);
    } catch (error) {
      notification("Error", blockersFrom(error).join(" "), "danger");
    }
  };

  const submitRecovery = async () => {
    if (!diagnosis || !evaluation || submittingRef.current) return;
    if (!confirmed || justification.trim().length < 10) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const result = await recoverPartner(diagnosis.partnerId, justification.trim());
      setLastResult(result);
      setModalOpen(false);
      notification(
        result.alreadyApplied ? "Sin cambios" : "Recuperación aplicada",
        result.alreadyApplied
          ? "La recuperación ya estaba aplicada. La baja programada se conserva."
          : "El colaborador quedó Activo y la baja programada se conservó.",
        "success",
      );
      setDiagnosis(await getPartnerDiagnosis(diagnosis.partnerId));
      await loadHistory(1);
      setHistoryPage(1);
    } catch (error) {
      notification("Recuperación bloqueada", blockersFrom(error).join(" "), "danger");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const action = diagnosis?.availableActions[0];
  const total = history?.total ?? 0;
  const limit = history?.limit ?? 10;
  const page = history?.page ?? 1;

  return (
    <Layout loading={loadingDiagnosis}>
      <Title
        baseTitle="Talento Humano"
        basePath="/"
        title="Centro de Incidencias"
      />
      <div className="card mb-3">
        <div className="card-header">Buscar colaborador</div>
        <div className="card-body">
          <div className="row g-2">
            <div className="col-md-5">
              <label className="form-label">ID o nombre</label>
              <input
                className="form-control"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") search();
                }}
                placeholder="ID o nombre"
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Sucursal</label>
              <select
                className="form-select"
                value={branchId}
                onChange={(event) => setBranchId(event.target.value)}
              >
                <option value="">Todas</option>
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3 d-flex align-items-end">
              <button className="btn btn-primary" disabled={searching || !query.trim()} onClick={search}>
                {searching ? "Buscando..." : "Buscar"}
              </button>
            </div>
          </div>
          {results.length > 0 && (
            <div className="list-group mt-3">
              {results.map((row) => (
                <button
                  key={row.partnerId}
                  type="button"
                  className="list-group-item list-group-item-action"
                  onClick={() => openDiagnosis(row.partnerId)}
                >
                  <strong>{row.fullName || "Sin nombre"}</strong>
                  <span className="ms-2 text-muted">#{row.partnerId}</span>
                  <span className="ms-2">{row.branchName ?? "Sin sucursal"}</span>
                  <span className="badge bg-secondary ms-2">{row.status ?? "Sin estatus"}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {diagnosis && (
        <div className="card mb-3">
          <div className="card-header d-flex justify-content-between">
            <span>Diagnóstico</span>
            <span>
              {diagnosis.alreadyApplied && (
                <span className="badge bg-info text-dark me-2">Recuperación ya aplicada</span>
              )}
              {action && <span className="badge bg-success">Recuperación disponible</span>}
              {!action && diagnosis.inconsistencies.length > 0 && (
                <span className="badge bg-warning text-dark">Recuperación bloqueada</span>
              )}
              {!action && diagnosis.inconsistencies.length === 0 && !diagnosis.alreadyApplied && (
                <span className="badge bg-secondary">Estado sin esta inconsistencia</span>
              )}
            </span>
          </div>
          <div className="card-body">
            <div className="row mb-3">
              <div className="col-md-4"><strong>Nombre:</strong> {diagnosis.fullName}</div>
              <div className="col-md-4"><strong>Sucursal:</strong> {diagnosis.branchName ?? "—"}</div>
              <div className="col-md-4"><strong>Estatus:</strong> {diagnosis.status ?? "—"}</div>
              <div className="col-md-4"><strong>Motivo:</strong> {diagnosis.reasonUnsubscribe || "—"}</div>
              <div className="col-md-4"><strong>Fecha efectiva:</strong> {diagnosis.effectiveDate ?? "—"}</div>
              <div className="col-md-4"><strong>Autorización:</strong> {diagnosis.authorizationStatus ?? "—"}</div>
            </div>

            {diagnosis.warnings.map((warning) => (
              <div key={warning} className="alert alert-info">{warning}</div>
            ))}
            {diagnosis.inconsistencies.length > 0 && (
              <div className="alert alert-warning">
                <strong>Inconsistencias</strong>
                <ul className="mb-0">
                  {diagnosis.inconsistencies.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            <h6>Solicitudes de baja</h6>
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Programada</th>
                  <th>Autorizada</th>
                  <th>Finalizada</th>
                  <th>Fecha</th>
                </tr>
              </thead>
              <tbody>
                {diagnosis.requests.map((request) => (
                  <tr key={request.id}>
                    <td>{request.id}</td>
                    <td>{request.temporalUnsubscribe ? "Sí" : "No"}</td>
                    <td>{request.isApproved ? "Sí" : "No"}</td>
                    <td>{request.isFinished ? "Sí" : "No"}</td>
                    <td>{request.banDate ? String(request.banDate).slice(0, 10) : "—"}</td>
                  </tr>
                ))}
                {diagnosis.requests.length === 0 && (
                  <tr><td colSpan={5}>Sin solicitudes</td></tr>
                )}
              </tbody>
            </table>

            <h6>Bajas programadas</h6>
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Fecha</th>
                  <th>Vinculada</th>
                  <th>Ejecutada</th>
                </tr>
              </thead>
              <tbody>
                {diagnosis.scheduledUnsubscribes.map((row) => (
                  <tr key={row.id}>
                    <td>{row.id}</td>
                    <td>{row.banDate ? String(row.banDate).slice(0, 10) : "—"}</td>
                    <td>{row.linked ? "Sí" : "No"}</td>
                    <td>{row.completed ? "Sí" : "No"}</td>
                  </tr>
                ))}
                {diagnosis.scheduledUnsubscribes.length === 0 && (
                  <tr><td colSpan={4}>Sin programadas</td></tr>
                )}
              </tbody>
            </table>

            <h6>Historial reciente de estatus</h6>
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Acción</th>
                  <th>Administrador</th>
                  <th>Justificación</th>
                </tr>
              </thead>
              <tbody>
                {diagnosis.recentHistory.map((row) => (
                  <tr key={row.id}>
                    <td>{row.createdAt ? new Date(row.createdAt).toLocaleString() : "—"}</td>
                    <td>{row.action}</td>
                    <td>{row.performedByName ?? "—"}</td>
                    <td>{row.reason || "—"}</td>
                  </tr>
                ))}
                {diagnosis.recentHistory.length === 0 && (
                  <tr><td colSpan={4}>Sin historial</td></tr>
                )}
              </tbody>
            </table>

            {action && (
              <button className="btn btn-success" onClick={openRecovery}>
                {operationLabel}
              </button>
            )}
            {lastResult && (
              <div className="alert alert-success mt-3 mb-0">
                Resultado: {lastResult.alreadyApplied ? "ya estaba aplicada" : "aplicada"}.
                Estatus {lastResult.status}. Solicitud {lastResult.requestId}. Programada {lastResult.programedId}.
                Fecha efectiva {lastResult.effectiveDate}. La baja programada se conservó.
              </div>
            )}
          </div>
        </div>
      )}

      <div className="card mb-3">
        <div className="card-header">Recuperaciones realizadas</div>
        <div className="card-body">
          <table className="table table-sm">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Colaborador</th>
                <th>Operación</th>
                <th>Administrador</th>
                <th>Justificación</th>
                <th>Resultado</th>
              </tr>
            </thead>
            <tbody>
              {(history?.results ?? []).map((row) => (
                <tr key={row.id}>
                  <td>{row.createdAt ? new Date(row.createdAt).toLocaleString() : "—"}</td>
                  <td>{row.partnerName ?? "—"} #{row.partnerId ?? "—"}</td>
                  <td>{row.operation === "REACTIVATE_SCHEDULED_UNSUBSCRIBE" ? operationLabel : row.operation}</td>
                  <td>{row.performedByName ?? "—"}</td>
                  <td>{row.reason ?? "—"}</td>
                  <td>{row.result ?? "—"}</td>
                </tr>
              ))}
              {(history?.results.length ?? 0) === 0 && (
                <tr><td colSpan={6}>Todavía no hay recuperaciones de este centro</td></tr>
              )}
            </tbody>
          </table>
          {total > 0 && (
            <div className="d-md-flex align-items-center">
              <div className="me-md-auto">
                Mostrando {(page - 1) * limit + 1} - {Math.min(page * limit, total)} de {total}
              </div>
              <ul className="pagination mb-0">
                <li className={`page-item ${page <= 1 ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => {
                      const next = page - 1;
                      setHistoryPage(next);
                      loadHistory(next);
                    }}
                  >
                    Anterior
                  </button>
                </li>
                <li className={`page-item ${page * limit >= total ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => {
                      const next = page + 1;
                      setHistoryPage(next);
                      loadHistory(next);
                    }}
                  >
                    Siguiente
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>

      <ModalContainer
        open={modalOpen}
        title={operationLabel}
        closeFn={() => {
          if (!submitting) setModalOpen(false);
        }}
      >
        <p>Esta operación reactivará al colaborador sin cancelar su baja programada.</p>
        <ul>
          <li>Colaborador: {diagnosis?.fullName}</li>
          <li>Estatus actual: {evaluation?.currentStatus}</li>
          <li>Estatus resultante: {evaluation?.targetStatus}</li>
          <li>Motivo de la suspensión: {diagnosis?.reasonUnsubscribe || "—"}</li>
          <li>Baja programada que se conservará: {evaluation?.programedId ?? "—"}</li>
          <li>Fecha efectiva: {evaluation?.effectiveDate ?? "—"}</li>
        </ul>
        <label className="form-label">Justificación</label>
        <textarea
          className="form-control mb-3"
          rows={3}
          value={justification}
          onChange={(event) => setJustification(event.target.value)}
          disabled={submitting}
        />
        <div className="form-check mb-3">
          <input
            id="confirm-recovery"
            className="form-check-input"
            type="checkbox"
            checked={confirmed}
            onChange={(event) => setConfirmed(event.target.checked)}
            disabled={submitting}
          />
          <label className="form-check-label" htmlFor="confirm-recovery">
            Confirmo esta recuperación
          </label>
        </div>
        <button
          className="btn btn-success"
          disabled={submitting || !confirmed || justification.trim().length < 10}
          onClick={submitRecovery}
        >
          {submitting ? "Procesando..." : operationLabel}
        </button>
      </ModalContainer>
    </Layout>
  );
};

export default IncidentRecoveryPage;
